import { createClient, type SupabaseClient } from "@supabase/supabase-js";

function readEnv(name: string) {
  const value = (import.meta as any).env?.[name];
  if (typeof value === "string" && value.trim()) return value.trim();

  try {
    const w = typeof window !== "undefined" ? (window as any) : null;
    const injected = w?.__ENV__?.[name];
    if (typeof injected === "string" && injected.trim()) return injected.trim();
  } catch {
    // ignore
  }

  return "";
}

export function getSupabaseConfig() {
  const directUrl = readEnv("VITE_SUPABASE_URL");
  const anonKey = readEnv("VITE_SUPABASE_ANON_KEY");

  // Default to direct Supabase URL. Use proxy only in embedded preview contexts
  // (or when explicitly forced), because Realtime websocket upgrades don't work
  // through our HTTP-only proxy.
  let url = directUrl;

  try {
    if (typeof window !== "undefined") {
      let origin = window.location?.origin ?? "";
      if (!origin || origin === "null" || !origin.startsWith("http")) {
        const href = window.location?.href ?? "";
        if (href && href.startsWith("http")) origin = new URL(href).origin;
      }

      const proxyUrl = origin && origin.startsWith("http") ? `${origin}/api/supabase` : "";

      let forceProxy = false;
      let disableProxy = false;
      try {
        disableProxy = (globalThis.localStorage?.getItem("supabase_disable_proxy") ?? "") === "1";
        forceProxy = (globalThis.localStorage?.getItem("supabase_use_proxy") ?? "") === "1";
      } catch {
        // ignore
      }

      const isEmbeddedPreview = (() => {
        try {
          return typeof window !== "undefined" && window.top !== window.self;
        } catch {
          return false;
        }
      })();

      // Never force proxy on normal top-level browsers because realtime websocket
      // upgrades fail on our HTTP-only proxy route.
      const shouldUseProxy = Boolean(proxyUrl) && !disableProxy && isEmbeddedPreview;
      url = shouldUseProxy ? proxyUrl : directUrl;

      // Self-heal old sessions that previously forced proxy usage.
      if (!isEmbeddedPreview) {
        try {
          globalThis.localStorage?.removeItem("supabase_use_proxy");
        } catch {
          // ignore
        }
      }
    }
  } catch {
    // ignore
  }

  return { url, anonKey, directUrl };
}

export function getSupabaseStorageKeyFromDirectUrl(directUrl: string) {
  try {
    const host = directUrl ? new URL(directUrl).hostname : "";
    const ref = host ? host.split(".")[0] : "";
    return ref ? `sb-${ref}-auth-token` : undefined;
  } catch {
    return undefined;
  }
}

const GLOBAL_SUPABASE_CLIENT_KEY = "__numeroloji_supabase_client__";

let _client: SupabaseClient | null = null;

export function resetSupabaseClient() {
  _client = null;
  try {
    delete (globalThis as any)[GLOBAL_SUPABASE_CLIENT_KEY];
  } catch {
    // ignore
  }
}

function toPlainHeaders(headers: HeadersInit | undefined) {
  const out: Record<string, string> = {};
  if (!headers) return out;

  if (headers instanceof Headers) {
    headers.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }

  if (Array.isArray(headers)) {
    for (const [k, v] of headers) out[k] = String(v);
    return out;
  }

  for (const [k, v] of Object.entries(headers)) out[k] = String(v);
  return out;
}

function parseRawHeaders(raw: string) {
  const h = new Headers();
  for (const line of raw.split(/\r?\n/)) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (!key) continue;
    h.append(key, value);
  }
  return h;
}

// Some hosted preview/iframe environments patch window.fetch and break network calls.
// Supabase-js allows passing a custom fetch implementation.
// We primarily use XHR because it's often less affected by fetch patches, but we keep a
// resilient wrapper that tries native fetch first (with a timeout) and falls back to XHR.
export async function xhrFetch(input: RequestInfo | URL, init?: RequestInit) {
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url;
  const method = (init?.method ?? (typeof input !== "string" && !(input instanceof URL) ? (input as Request).method : "GET")) || "GET";
  const headers = toPlainHeaders(init?.headers ?? (typeof input !== "string" && !(input instanceof URL) ? (input as Request).headers : undefined));
  const body = init?.body;

  return await new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.responseType = "arraybuffer";

    const creds = init?.credentials;
    xhr.withCredentials = creds === "include";

    for (const [k, v] of Object.entries(headers)) {
      try {
        xhr.setRequestHeader(k, v);
      } catch {
        // ignore
      }
    }

    const signal = init?.signal;
    const onAbort = () => {
      try {
        xhr.abort();
      } catch {
        // ignore
      }
      // Avoid noisy unhandled AbortError rejections; return a synthetic response.
      resolve(new Response(null, { status: 499, statusText: "AbortError" }));
    };

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.onerror = () => {
      if (signal) signal.removeEventListener("abort", onAbort);
      reject(new TypeError("Failed to fetch"));
    };

    xhr.onload = () => {
      if (signal) signal.removeEventListener("abort", onAbort);
      const respHeaders = parseRawHeaders(xhr.getAllResponseHeaders());

      const status = xhr.status;
      const statusText = xhr.statusText;

      // The Response constructor throws if a body is provided for null-body statuses (204/205/304).
      const nullBodyStatus = status === 204 || status === 205 || status === 304;
      const respBody = nullBodyStatus ? null : xhr.response;

      resolve(
        new Response(respBody, {
          status,
          statusText,
          headers: respHeaders,
        }),
      );
    };

    try {
      xhr.send(body as any);
    } catch (err) {
      if (signal) signal.removeEventListener("abort", onAbort);
      // Some browsers can throw AbortError synchronously from xhr.send when the request is canceled.
      // Avoid surfacing it as an unhandled rejection.
      if (isAbortError(err)) {
        resolve(new Response(null, { status: 499, statusText: "AbortError" }));
        return;
      }
      reject(err);
    }
  });
}

function isAbortError(err: unknown) {
  const anyErr = err as any;
  const name = String(anyErr?.name ?? "");
  const msg = String(anyErr?.message ?? "");
  return name === "AbortError" || msg.toLowerCase().includes("signal is aborted");
}

function isNetworkyError(err: unknown) {
  if (!err) return false;
  if (isAbortError(err)) return true;
  const anyErr = err as any;
  const name = String(anyErr?.name ?? "");
  const msg = String(anyErr?.message ?? "");
  return name === "TypeError" || msg.toLowerCase().includes("failed to fetch");
}

export async function resilientFetch(input: RequestInfo | URL, init?: RequestInit) {
  // Some embedded/preview environments patch native fetch; prefer XHR there to avoid long hangs.
  const preferXhr = (() => {
    try {
      return typeof window !== "undefined" && window.top !== window.self;
    } catch {
      return false;
    }
  })();

  try {
    const tryXhr = async () => {
      try {
        return await xhrFetch(input, init);
      } catch (err) {
        if (isAbortError(err)) return new Response(null, { status: 499, statusText: "AbortError" });
        throw err;
      }
    };

    const tryNative = async () => {
      const nativeFetch = (globalThis as any)?.fetch as typeof fetch | undefined;
      if (typeof nativeFetch !== "function") {
        throw new TypeError("Failed to fetch");
      }

      // Do not use AbortController for our own timeouts here.
      // Some environments surface aborts as noisy errors. Instead, time out via Promise.race
      // and fall back while letting the original fetch resolve in the background.
      const timeoutMs = preferXhr ? 1500 : 10000;
      const timeoutPromise = new Promise<Response>((_, reject) => {
        globalThis.setTimeout(() => reject(new Error("native_fetch_timeout")), timeoutMs);
      });

      return await Promise.race([nativeFetch(input as any, init as any), timeoutPromise]);
    };

    if (preferXhr) {
      // Preview/iframe: avoid patched native fetch entirely.
      return await tryXhr();
    }

    // Normal browsers: native fetch first.
    try {
      return await tryNative();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as any)?.message ?? "");
      if (msg !== "native_fetch_timeout") {
        if (!isNetworkyError(err)) throw err;
        if (isAbortError(err)) return new Response(null, { status: 499, statusText: "AbortError" });
      }
    }

    return await tryXhr();
  } catch (err) {
    if (isAbortError(err)) return new Response(null, { status: 499, statusText: "AbortError" });
    throw err;
  }
}

const _memoryStorage = new Map<string, string>();
const safeStorage = {
  getItem: (key: string) => {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return _memoryStorage.get(key) ?? null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      _memoryStorage.set(key, value);
    }
  },
  removeItem: (key: string) => {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      _memoryStorage.delete(key);
    }
  },
};

export function getSupabaseClient(): SupabaseClient {
  const globalClient = (globalThis as any)[GLOBAL_SUPABASE_CLIENT_KEY] as SupabaseClient | undefined;
  if (globalClient) {
    _client = globalClient;
    return globalClient;
  }

  if (_client) {
    (globalThis as any)[GLOBAL_SUPABASE_CLIENT_KEY] = _client;
    return _client;
  }

  const { url, anonKey, directUrl } = getSupabaseConfig();
  if (!url || !anonKey) {
    throw new Error("Supabase config missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.");
  }

  const storageKey = getSupabaseStorageKeyFromDirectUrl(directUrl);

  _client = createClient(url, anonKey, {
    ...(typeof window !== "undefined" ? { global: { fetch: resilientFetch as any } } : null),
    auth: {
      storage: safeStorage,
      ...(storageKey ? { storageKey } : null),
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    },
  });

  (globalThis as any)[GLOBAL_SUPABASE_CLIENT_KEY] = _client;
  return _client;
}
